import React from "react";
import { money } from "./fields";
import { YearTable, Warnings } from "./YearTable";

const IUL_ONLY = ["iul_taxable", "mec", "iul_lapse"];
const FIA_ONLY = ["fia_depleted"];

const Stat = ({ label, value, testid, gold }) => (
  <div className="bg-exodus-surface p-5">
    <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
    <p data-testid={testid} className={`font-display text-2xl sm:text-3xl mt-2 ${gold ? "gold-text" : ""}`}>{value}</p>
  </div>
);

export const StepResults = ({ inputs, result, onAckMec }) => {
  const isIul = inputs.product === "iul";
  const c = result.client;
  const cmp = result.compare.find((x) => x.key === inputs.product);
  const iul = result.iul, fia = result.fia;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-exodus-border border border-exodus-border">
        <Stat testid="ro-res-income" gold label={isIul ? (iul.mec ? "Loan income / yr (after MEC tax)" : "Tax-free loan income / yr") : (fia.rider_income ? "Lifetime income / yr (after tax)" : "After-tax income / yr")} value={money(cmp.annual_net_income)} />
        <Stat testid="ro-res-years" label={`Years of income (${c.income_start_age}-${c.plan_to_age})`} value={cmp.income_years} />
        <Stat testid="ro-res-total" label={`Total after-tax income to ${c.plan_to_age}`} value={money(cmp.total_net_income)} />
        <Stat testid="ro-res-legacy" label={`Legacy at ${c.plan_to_age}`} value={money(cmp.legacy_at_plan_age)} />
      </div>
      {isIul ? (
        <div className="grid sm:grid-cols-3 gap-px bg-exodus-border border border-exodus-border">
          <Stat testid="ro-res-face" label="Modeled death benefit" value={money(iul.face_amount)} />
          <Stat testid="ro-res-funding-tax" label="Taxes to fund the policy" value={money(iul.funding_tax)} />
          <Stat testid="ro-res-mec" label="MEC status" value={iul.mec ? `Likely MEC (yr ${iul.mec_year})` : "Not indicated"} />
        </div>
      ) : (
        <div className={`grid gap-px bg-exodus-border border border-exodus-border ${fia.rider_income ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          <Stat testid="ro-res-credited" label="Assumed credited rate" value={`${fia.credited_rate.toFixed(2)}%`} />
          {fia.rider_income ? (
            <>
              <Stat testid="ro-res-income-base" label="Income base at income start" value={money(fia.rows.find((r) => r.age === c.income_start_age)?.income_base)} />
              <Stat testid="ro-res-gross-income" label="Guaranteed income (gross)" value={money(fia.rider_income)} />
            </>
          ) : (
            <Stat testid="ro-res-surr-years" label="Surrender period" value={`${inputs.fia.surrender_years} years`} />
          )}
        </div>
      )}

      <Warnings items={result.warnings.filter((w) => !(isIul ? FIA_ONLY : IUL_ONLY).includes(w.code))} />
      {isIul && iul.mec && (
        <label data-testid="ro-mec-ack" className="flex items-start gap-3 text-sm border border-red-500/40 p-4 cursor-pointer">
          <input data-testid="ro-mec-ack-checkbox" type="checkbox" checked={!!inputs.iul.mec_acknowledged} onChange={(e) => onAckMec(e.target.checked)} className="mt-1 accent-[#C6A15B]" />
          <span>I understand this design is likely a MEC, so loans may be taxable and penalized. Show the values anyway.</span>
        </label>
      )}

      {(!isIul || !iul.mec || inputs.iul.mec_acknowledged) && (
        <div>
          <p className="eyebrow mb-3">Year-by-year {isIul ? "policy values" : "contract values"} · highlighted row = income start</p>
          <YearTable testid="ro-year-table" kind={inputs.product} rows={isIul ? iul.rows : fia.rows} incomeStart={c.income_start_age}
            flagAge={isIul ? iul.lapse_age : null} />
        </div>
      )}
    </div>
  );
};
